import React, { useCallback, useEffect, useState, Fragment, Component } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, LoaderIcon, AlertCircleIcon, BookOpenIcon, ClockIcon, TrendingUpIcon, GlobeIcon, ShieldCheckIcon, HeartIcon, UsersIcon, CheckCircleIcon, BriefcaseIcon, DollarSignIcon, ChevronDownIcon, ChevronUpIcon, CrossIcon } from 'lucide-react';
import { degreeInterestOptions, focusOptions, getSpecializationsForFocus } from '../data/degrees';
import { getGuideBySlug } from '../data/guides';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { TrustSignals } from '../components/TrustSignals';
import { useFormState } from '../hooks/useFormState';
import { usePageMeta } from '../hooks/usePageMeta';
const benefitIconMap: Record<string, React.ElementType> = {
  ClockIcon,
  TrendingUpIcon,
  GlobeIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  HeartIcon,
  UsersIcon,
  CheckCircleIcon
};
export function GuidePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const guide = slug ? getGuideBySlug(slug) : undefined;
  usePageMeta(guide?.metaTitle || 'Guide | OnlineCollegeGuide', guide?.metaDescription || 'Explore accredited online colleges and degree programs.', {
    ogImage: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80',
    ogType: 'article'
  });
  const {
    formData,
    errors,
    updateFormData,
    validateHeroForm
  } = useFormState({
    areaOfStudy: guide?.focusValue || ''
  });
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  // API state
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
  };
  const fetchResults = useCallback(async (degreeInterest: string, areaStudy: string, mProgram: string) => {
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudy || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, []);
  // Auto-fetch on page load with the guide's focus value
  useEffect(() => {
    if (guide?.focusValue) {
      fetchResults('', guide.focusValue, guide.defaultSpecialization || '');
    }
  }, [guide?.focusValue, guide?.defaultSpecialization, fetchResults]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const handleSidebarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm()) {
      fetchResults(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  if (!guide) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-1299" data-mp="1yfpocp6c0yd6">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1300" data-mp="1tgw6kkk0il77">Guide Not Found</h1>
        <p className="text-gray-600 mb-8" data-id="element-1301" data-mp="1tiz6bwayvsp4">
          We couldn't find the guide you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1302" data-mp="27eb5qh02i7cd">
          Return Home
        </Link>
      </div>;
  }
  return <main className="flex-grow bg-light pb-16" data-id="element-1303" data-mp="5rhv8bes9tsv">
      {/* Page Header */}
      <div className="bg-navy text-white py-12" data-id="element-1304" data-mp="5pewn2zkescj">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1305" data-mp="ahyck6lq5zki">
          {/* Breadcrumbs */}
          <nav className="flex text-sm text-gray-300 mb-6" aria-label="Breadcrumb" data-id="element-1306" data-mp="fhknuahzt5qo">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-1307" data-mp="3e08e1jbjau8k">
              <li className="inline-flex items-center" data-id="element-1308" data-mp="3ix89z1eo6gly">
                <Link to="/" className="inline-flex items-center hover:text-white transition-colors" data-id="element-1309" data-mp="3izusfw33mgiy">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-1310" data-mp="3rzr7r73lm31c" />
                  Home
                </Link>
              </li>
              <li data-id="element-1311" data-mp="136c88fpccjsw">
                <div className="flex items-center" data-id="element-1312" data-mp="138ygyinyx5cp">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1313" data-mp="177fpn6n1enw8" />
                  <span className="ml-1 md:ml-2" data-id="element-1314" data-mp="2rqi386qe85mm">Guides</span>
                </div>
              </li>
              <li aria-current="page" data-id="element-1315" data-mp="2nowijz7man1v">
                <div className="flex items-center" data-id="element-1316" data-mp="2npzdci8299n2">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1317" data-mp="2iy7uktv5c87h" />
                  <span className="ml-1 md:ml-2 text-white font-medium" data-id="element-1318" data-mp="2iwmzn34x6n93">
                    {guide.breadcrumbLabel}
                  </span>
                </div>
              </li>
            </ol>
          </nav>

          {guide.heroEyebrow && <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs font-semibold mb-4 text-[#D4A943] uppercase tracking-widest" data-id="element-1319" data-mp="2416zcgdqxv49">
              <CrossIcon className="w-3.5 h-3.5 mr-2" data-id="element-1320" data-mp="2016k4titpfw5" />
              {guide.heroEyebrow}
            </div>}

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" data-id="element-1321" data-mp="3ud3a0i1madp3">
            {guide.heroHeadline}
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl" data-id="element-1322" data-mp="3pfl5r4yjomnf">
            {guide.heroDescription}
          </p>
        </div>
      </div>

      <TrustSignals data-id="element-1323" data-mp="cbeg97gj6zsu" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1324" data-mp="h3z4zukkympi">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1325" data-mp="h6buqtwky1d4">
          {/* Main Content - Listings */}
          <div className="lg:w-3/4 order-2 lg:order-1" data-id="element-1326" data-mp="3aq0a77kupdb">
            <h2 className="text-2xl font-bold text-navy mb-6" data-id="element-1327" data-mp="8cyv7940g1i2">
              Top {guide.title} of {new Date().getFullYear()}
            </h2>

            {/* Loading State */}
            {isLoading && <div className="flex flex-col items-center justify-center py-16" data-id="element-1328" data-mp="101s8axz48ynf">
                <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-1329" data-mp="m43n1p14zphu" />
                <p className="text-gray-600 font-medium" data-id="element-1330" data-mp="r25464f05eqo">
                  Finding your best school matches...
                </p>
              </div>}

            {/* Error State */}
            {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-1331" data-mp="3btfeqadrapit">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1332" data-mp="347s8q3ux96k3" />
                <div data-id="element-1333" data-mp="2z8yyckaxlvo5">
                  <p className="text-red-800 font-medium" data-id="element-1334" data-mp="2z9qrzpkyxjbs">{apiError}</p>
                  <p className="text-red-600 text-sm mt-1" data-id="element-1335" data-mp="3d8qil2s1k8al">
                    Try adjusting your search criteria or check back later.
                  </p>
                </div>
              </div>}

            {/* Results */}
            {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-1336" data-mp="2gku0ftgyh4x9">
                {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-1337" data-mp="2ubp7d07w9by7" data-mpi={idx}>
                    <MatchedSchoolCard school={school} index={idx} data-id="element-1338" data-mp="21t1qjwzxkj2x" />
                    {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-1339" data-mp="1c9pwqhtzxu73" />}
                  </Fragment>)}
              </div>}

            {/* Empty State */}
            {!isLoading && !apiError && hasSearched && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-1340" data-mp="1q97q7ai8lvms">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-1341" data-mp="1v8u1vfif33th">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-1342" data-mp="1z9nswhr5oyql" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1343" data-mp="240dwbpg0urin">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-1344" data-mp="28ynrw15nru4s">
                    Try adjusting your degree level or program selection in the
                    sidebar to find more results.
                  </p>
                </div>}
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/4 order-1 lg:order-2" data-id="element-1345" data-mp="7bupkiuj2mwd">
            <div className="sticky top-24 space-y-6" data-id="element-1346" data-mp="2bq4sgml760c">
              {/* Lead Form */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden" data-id="element-1347" data-mp="2e2u5euocinf">
                <div className="bg-[#1B365D] px-6 py-4" data-id="element-1348" data-mp="3tjuhk6h2hh6j">
                  <h3 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-1349" data-mp="3ok83xvxbtyx7">
                    Get Matched Fast
                  </h3>
                  <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1350" data-mp="l2hi1t9p3gk0">
                    Find the right program for you
                  </p>
                </div>

                <form onSubmit={handleSidebarSubmit} className="p-5 space-y-4" data-id="element-1351" data-mp="g9esdw81gr93">
                  <div data-id="element-1352" data-mp="bc4r9jbaer5f">
                    <select id="sidebar-degreeInterest" value={formData.degreeInterest} onChange={e => updateFormData('degreeInterest', e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1353" data-mp="bak6glerymkr">
                      <option value="" data-id="element-1354" data-mp="2gangbsjwyvf2">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1355" data-mp="2u93lvg61ifvm">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1356" data-mp="342u6g9z1q661">
                    <select id="sidebar-areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1357" data-mp="2z1em6k4p03op">
                      <option value="" data-id="element-1358" data-mp="3inyjzal1u8q6">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1359" data-mp="3nm03txqauc2p">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1360" data-mp="3dpeytcea50yv">
                    <select id="sidebar-specialization" value={formData.specialization} onChange={e => updateFormData('specialization', e.target.value)} disabled={!formData.areaOfStudy} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1361" data-mp="3dpwsc19k5133">
                      <option value="" data-id="element-1362" data-mp="2247helpody91">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-1363" data-mp="2pubhij3nnfva">
                          {opt.type}
                        </option>)}
                    </select>
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full bg-[#D4A943] hover:bg-[#B38B2E] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" data-id="element-1364" data-mp="2fuko6kqtz6ws">
                    {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1365" data-mp="3q1qgbce5trqi" />}
                    {isLoading ? 'Searching...' : 'Search Programs'}
                  </button>
                </form>
              </div>

              {/* What to Look For */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1366" data-mp="3nym7udc0nv0">
                <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1367" data-mp="7lnyj5azc4n7">
                  {guide.sidebarChecklistTitle}
                </h4>
                <ul className="space-y-2.5" data-id="element-1368" data-mp="hm6n2ddho9bg">
                  {guide.sidebarChecklist.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-1369" data-mp="2eeqbd14via34" data-mpi={i}>
                      <CheckCircleIcon className="w-4 h-4 text-[#059668] flex-shrink-0 mt-0.5" data-id="element-1370" data-mp="2jeuuz012vkxk" />
                      <span data-id="element-1371" data-mp="dqkis5yh54qi">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Related Links */}
              {guide.relatedLinks.length > 0 && <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1372" data-mp="inuc3wozi0jm">
                  <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1373" data-mp="3r3nj9kxfdzo0">
                    Related
                  </h4>
                  <ul className="space-y-2" data-id="element-1374" data-mp="3m5m2j6nh2cfp">
                    {guide.relatedLinks.map(link => <li key={link.href} data-id="element-1375" data-mp="3rlf71skfzad">
                        <Link to={link.href} className="text-sm flex items-center text-gray-600 hover:text-crimson" data-id="element-1376" data-mp="3sehnqkhs2mp">
                          <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-1377" data-mp="37dk8w4bzwfo4" />
                          {link.label}
                        </Link>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Guide Content Sections */}
      <div className="bg-white border-t border-gray-200 mt-12 pt-16 pb-20" data-id="element-1378" data-mp="2jczzljvguu8w">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1379" data-mp="2oaic1k8ozrln">
          {/* Overview */}
          <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-1380" data-mp="3c0vxgrwrus79">
            <h2 className="text-3xl font-bold text-navy mb-6" data-id="element-1381" data-mp="3byjf4dn3qfto">
              {guide.overviewHeading}
            </h2>
            <div className="prose prose-lg text-gray-600 mx-auto leading-relaxed space-y-6 text-left" data-id="element-1382" data-mp="322gfpfjmwvaf">
              {guide.overviewParagraphs.map((p, i) => <p key={i} data-id="element-1383" data-mp="36zyg2hjl626t" data-mpi={i}>{p}</p>)}
            </div>
          </div>

          {/* Benefits */}
          <div className="mb-20 bg-gray-50 rounded-2xl p-8 sm:p-12 border border-gray-200" data-id="element-1384" data-mp="1qlyihd87qomq">
            <div className="text-center mb-12" data-id="element-1385" data-mp="1vnxiqe8np5fg">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1B365D] mb-4" data-id="element-1386" data-mp="23dre2fd7y3j">
                {guide.benefitsHeading}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1387" data-mp="3taglp2qi2khz">
                {guide.benefitsIntro}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1388" data-mp="3ogvmx8h31mhq">
              {guide.benefits.map((benefit, i) => {
              const IconComponent = benefitIconMap[benefit.icon] || BookOpenIcon;
              return <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-1389" data-mp="3ahvx3xhu31jz" data-mpi={i}>
                    <div className="w-12 h-12 bg-[#1B365D]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-1390" data-mp="30lssmkzieqtv">
                      <IconComponent className="w-6 h-6 text-[#1B365D]" data-id="element-1391" data-mp="13ry241ubvqd7" />
                    </div>
                    <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1392" data-mp="yuxvnptkgllk">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1393" data-mp="16bcm5vypj7jb">
                      {benefit.description}
                    </p>
                  </div>;
            })}
            </div>
          </div>

          {/* Careers */}
          <div className="mb-20" data-id="element-1394" data-mp="1l2l9fyf50gta">
            <div className="text-center mb-10" data-id="element-1395" data-mp="1l1jsxefcr246">
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1396" data-mp="nf2xkeq6iyl2">
                {guide.careersHeading}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1397" data-mp="sepfh1gyd307">
                {guide.careersIntro}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1398" data-mp="xetwcslrvzqy">
              {guide.careers.map((career, i) => <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-200 flex flex-col h-full" data-id="element-1399" data-mp="28sfie4wc5l89" data-mpi={i}>
                  <div className="w-12 h-12 bg-[#1B365D]/10 rounded-lg flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1400" data-mp="2yu9s6dtns73b">
                    <BriefcaseIcon className="w-6 h-6" data-id="element-1401" data-mp="2txjaxn64b5gb" />
                  </div>
                  <h3 className="text-lg font-bold text-navy mb-2 leading-tight" data-id="element-1402" data-mp="38lw8gokuswcx">
                    {career.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-6 flex-grow" data-id="element-1403" data-mp="33s1ix4ikhn5x">
                    {career.desc}
                  </p>
                  <div className="pt-4 border-t border-gray-200 mt-auto" data-id="element-1404" data-mp="3dk7ip0f298vo">
                    <div className="flex items-center text-sm font-semibold text-gray-800" data-id="element-1405" data-mp="3dm0jhz2zsyvr">
                      <DollarSignIcon className="w-4 h-4 text-[#059668] mr-1" data-id="element-1406" data-mp="3rhmcehcck2tg" />
                      {career.salary}
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1407" data-mp="1x55yqvh81mek">
                      Estimated Annual Salary
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* FAQs */}
          <div className="max-w-3xl mx-auto" data-id="element-1408" data-mp="7pc05znaldjc">
            <div className="text-center mb-10" data-id="element-1409" data-mp="co6hex5m2u1c">
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1410" data-mp="cmvet0nwjsbt">
                {guide.faqHeading}
              </h2>
              <p className="text-gray-600" data-id="element-1411" data-mp="3u2ak06pyd9ry">{guide.faqIntro}</p>
            </div>

            <div className="space-y-4" data-id="element-1412" data-mp="2w0vo55yd8ul">
              {guide.faqs.map((faq, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm transition-all duration-200" data-id="element-1413" data-mp="3l1vpqhv0gkcb" data-mpi={index}>
                  <button className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-inset focus:ring-navy" onClick={() => toggleFaq(index)} aria-expanded={openFaqIndex === index} data-id="element-1414" data-mp="3b6tyngh6d873">
                    <span className="font-semibold text-navy pr-8" data-id="element-1415" data-mp="1eawg67ra64j2">
                      {faq.q}
                    </span>
                    {openFaqIndex === index ? <ChevronUpIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-1416" data-mp="2mpgdeap5sri4" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500 flex-shrink-0" data-id="element-1417" data-mp="28ybjzqcdwvg8" />}
                  </button>
                  {openFaqIndex === index && <div className="px-6 pb-5 pt-0" data-id="element-1418" data-mp="31lnx79wnnqwv">
                      <p className="text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4 mt-2" data-id="element-1419" data-mp="35mhjxzyd2zo6">
                        {faq.a}
                      </p>
                    </div>}
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </main>;
}